import { CClient } from '@common/utils'
import customStyles from '@components/book-trial-class/trial-booking-component.module.sass'
import styles from '@components/common/dotted-card-bg.module.sass'
import Spinner from '@components/common/spinner'
import { FormBuilder } from '@components/competitions/dynamic-forms'
import { S3FileUploader } from '@components/competitions/s3-file-uploader'
import { validateFieldsData } from '@containers/competitions/validators'
import { useSessionContext } from '@contexts/session'
import useSegment, { SegmentEventEnum } from '@hooks/useSegment'
import * as Sentry from '@sentry/node'
import classNames from 'classnames'
import $ from 'jquery'
import Router from 'next/router'
import { useRef, useState } from 'react'
import swal from 'sweetalert'

type Props = {
  slug: string
  formData: string
  linkedEntityId: number
}

export const CompetitionSubmissionContainer = ({
  slug,
  formData,
  linkedEntityId,
}: Props): JSX.Element => {
  const dynamicFormRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [fileFormErrors, setFileFormErrors] = useState({})
  const { id: userId } = useSessionContext()
  const track = useSegment()
  // Take out only file fields
  const formDataObject = JSON.parse(formData)
  const fileFields = formDataObject.filter((obj) => {
    return obj.type === 'file'
  })
  const submitBtnClass = classNames({
    'btn btn-orange w-full py-3 text-lg': true,
    'opacity-50 cursor-not-allowed flex justify-center': loading,
  })

  const getDynamicFormData = () => {
    const formRenderInstance = $(dynamicFormRef.current).formRender('userData')
    const formBody = {}
    formRenderInstance.map((obj) => {
      if (typeof obj.userData === 'undefined') obj.userData = ['']
      formBody[obj.name] = obj.userData && obj.userData[0]
    })
    return formBody
  }

  const getDynamicFormValidationData = () => {
    const formRenderInstance = $(dynamicFormRef.current).formRender('userData')
    const formBody = {}
    formRenderInstance.map((obj) => {
      if (typeof obj.userData === 'undefined') obj.userData = ['']
      formBody[obj.name] = {
        value: obj.userData && obj.userData[0],
        required: obj.required,
      }
    })
    return formBody
  }

  const showDynamicErrors = (errors) => {
    const previousErrorElements = document.getElementsByClassName(
      'custom-error'
    )
    while (previousErrorElements.length > 0) previousErrorElements[0].remove()

    const errorsArray = Object.entries(errors)
    errorsArray.map((element) => {
      const [name, value] = element
      const elem = document.querySelector(`[name=${name}]`)
      const parentDiv = elem.parentElement
      const errorNode = document.createElement('div')
      errorNode.classList.add(
        'text-red-200',
        'text-sm',
        'leading-4',
        'font-400',
        'py-2',
        'custom-error'
      )
      errorNode.innerHTML = `${value}`
      parentDiv.appendChild(errorNode)
    })
  }

  const getFilesData = () => {
    const filesFormBody = {}
    fileFields.map((obj) => {
      const elem: HTMLElement = document.querySelector(
        `#${obj.name} .react-fine-uploader-filename`
      )
      const filename = elem && `media/userdata/${userId}/${elem.innerText}/`
      filesFormBody[obj.name] = filename || ''
    })
    return filesFormBody
  }

  const getFilesValidationData = () => {
    const filesFormBody = {}
    fileFields.map((obj) => {
      const elem: HTMLElement = document.querySelector(
        `#${obj.name} .react-fine-uploader-filename`
      )
      const filename = elem && `media/userdata/${userId}/${elem.innerText}/`
      filesFormBody[obj.name] = {
        value: filename || '',
        required: obj.required,
      }
    })
    return filesFormBody
  }

  const submitCompetition = async () => {
    const dynamicFormBody = getDynamicFormData()
    const filesFormBody = getFilesData()
    const formBody = {
      hackathon: linkedEntityId,
      data: { ...dynamicFormBody, ...filesFormBody },
    }

    const url = `/api/v1/competitions/${slug}/submissions/`
    try {
      const response = await CClient(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formBody),
      })
      if (response.ok) {
        Router.push(`/competitions/${slug}/submit/complete`)
      } else {
        throw new Error(response.statusText)
      }
    } catch (error) {
      swal({
        icon: 'error',
        title: 'Sorry!',
        text:
          'There was error while submitting your information. Please refresh the page and try again.',
      })
      Sentry.captureException(error)
    }
  }

  const handleSubmissionFormSubmit = () => {
    track(SegmentEventEnum.HACKATHON_SUBMISSION_CLICK_SUBMIT)
    setLoading(true)
    const filesValidationFormBody = getFilesValidationData()
    const dynamicFormValidationBody = getDynamicFormValidationData()

    const dynamicValuesArray = Object.entries(dynamicFormValidationBody)
    const dynamicFormErrors = validateFieldsData(dynamicValuesArray)

    const fileValidationArray = Object.entries(filesValidationFormBody)
    const fileValidationErrors = validateFieldsData(fileValidationArray)

    showDynamicErrors(dynamicFormErrors)
    if (Object.keys({ ...dynamicFormErrors, ...fileValidationErrors }).length) {
      setFileFormErrors(fileValidationErrors)
      setLoading(false)
      return
    }
    submitCompetition()
  }

  return (
    <div
      className={`${styles.dottedCardWrapper} ${customStyles.dottedCardWrapper} w-full flex-1 sm:w-1/2 my-10 mx-4`}
    >
      <div className="w-full px-5 py-6 bg-white border border-orange-300 rounded-lg shadow-sm sm:px-10">
        <h1 className="py-4 text-3xl text-center border-b text-orange font-600 border-grey-400">
          Enter below details
        </h1>
        <FormBuilder formData={formData} dynamicFormRef={dynamicFormRef} />
        {fileFields && (
          <>
            {fileFields.length !== 0 && (
              <p className="text-grey text-md font-600">
                Please upload following documents:
              </p>
            )}
            {fileFields.map((obj) => (
              <div key={obj.name}>
                <div className="mt-3 mb-2 text-grey text-md font-600">
                  {obj.label}
                  {obj.required && <span className="text-red-200">*</span>}
                </div>
                <S3FileUploader
                  fieldName={obj.name}
                  error={fileFormErrors[obj.name]}
                />
              </div>
            ))}
          </>
        )}
        <div className="mt-8 mb-5">
          <button
            type="button"
            className={submitBtnClass}
            onClick={handleSubmissionFormSubmit}
            disabled={loading}
          >
            {loading ? (
              <Spinner classes="text-white h-5 w-5" />
            ) : (
              'Submit Project'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
